import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { promoAdjustmentInfiniteQueryOptions } from '../api/promotion.api';
import { getPromoAdjustmentColumns } from '../components/PromoColumns';
import { PromoAdjustmentFilterPanel } from '../components/PromoAdjustmentFilterPanel';
import { useAdjustmentFilters } from '../hooks/useAdjustmentFilters';
import { getPromoAdjustmentStatCards } from '../utils/promotionStatCards';

export function PromoAdjustmentPage() {
  const controller = useAdjustmentFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(promoAdjustmentInfiniteQueryOptions(controller.params));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedItemId = useRevealLastViewedRow('promoItems');

  const columns = useMemo(
    () => getPromoAdjustmentColumns(controller.drillParams, controller.params.search),
    [controller.drillParams, controller.params.search],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Promotional Item Adjustment"
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: 'Item name',
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <PromoAdjustmentFilterPanel filters={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the report"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid stats={getPromoAdjustmentStatCards(firstPageMeta)} isLoading={isLoading} />
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            isRowHighlighted={(item) => item.promoItemId === lastViewedItemId}
            emptyMessage="No promotional item went out in this period"
            emptyHint="Try a wider date range."
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
